import { useState, useCallback } from 'react';

/**
 * Custom hook for managing a selection state of items
 * 
 * @param {Array} initialSelection - Optional array of initially selected items
 * @returns {Object} Selection state and helper functions
 */
const useSelectionState = (initialSelection = []) => {
  const [selectedItems, setSelectedItems] = useState(initialSelection);

  /**
   * Toggle an item's selection status
   * @param {any} item - The item to toggle
   */
  const toggleItem = useCallback((item) => {
    setSelectedItems(prev => {
      if (prev.includes(item)) {
        return prev.filter(i => i !== item);
      } else {
        return [...prev, item];
      }
    });
  }, []);

  /**
   * Check if an item is selected
   * @param {any} item - The item to check
   * @returns {boolean} Whether the item is selected
   */
  const isSelected = useCallback((item) => {
    return selectedItems.includes(item);
  }, [selectedItems]);

  /**
   * Select all items from a list
   * @param {Array} items - The list of items to select
   */
  const selectAll = useCallback((items) => {
    setSelectedItems(items);
  }, []);

  /**
   * Deselect all items
   */
  const deselectAll = useCallback(() => {
    setSelectedItems([]);
  }, []);

  /**
   * Add multiple items to selection
   * @param {Array} items - Items to add to selection
   */
  const addItems = useCallback((items) => {
    setSelectedItems(prev => {
      const newItems = items.filter(item => !prev.includes(item));
      return [...prev, ...newItems];
    });
  }, []);

  /**
   * Remove multiple items from selection
   * @param {Array} items - Items to remove from selection
   */
  const removeItems = useCallback((items) => {
    setSelectedItems(prev => 
      prev.filter(item => !items.includes(item))
    );
  }, []);

  return {
    selectedItems,
    setSelectedItems,
    toggleItem,
    isSelected,
    selectAll,
    deselectAll,
    addItems,
    removeItems,
    count: selectedItems.length
  };
};

export default useSelectionState; 